import { create } from "zustand";
import * as Types from "@/models/types";
import type { ContentLanguage } from "@/models/types";
import { persist, createJSONStorage, StateStorage } from "zustand/middleware";
import { useRouter } from "@/_router";
import { Preferences } from "@capacitor/preferences";
import { Capacitor } from "@capacitor/core";
import * as serverClient from "@/models/serverClient";
import { useAuth } from "@/models/useAuth";
import { useUISlice } from "@/models/useUISlice";
import { i18n } from "@/locales";
import toast from "react-hot-toast";
import {
  REVENUE_CAT_ANDROID_API_KEY,
  REVENUE_CAT_IOS_API_KEY,
} from "@/models/utils";
import { Purchases, PurchasesPackage } from "@revenuecat/purchases-capacitor";
import { getPlatform, isNativeApp } from "./platform";
import { logFunnelEvent } from "@/fetchData/datadog";

const storage: StateStorage = {
  getItem: async (name: string): Promise<string | null> => {
    try {
      const { value } = await Preferences.get({ key: name });
      return value;
    } catch (error) {
      console.warn("Preferences.get error:", error);
      return null;
    }
  },
  setItem: async (name: string, value: string): Promise<void> => {
    try {
      await Preferences.set({ key: name, value });
    } catch (error) {
      console.warn("Preferences.set error:", error);
    }
  },
  removeItem: async (name: string): Promise<void> => {
    try {
      await Preferences.remove({ key: name });
    } catch (error) {
      console.warn("Preferences.remove error:", error);
    }
  },
};

type State = {
  _hasHydrated: boolean;
  remoteConfig?: Types.RemoteConfig;
  loginPromptDismissed?: boolean;
  userToken?: string;
  webSubscriptionPlans: {
    weekly: Types.SubscriptionPlan;
    annual: Types.SubscriptionPlan;
  };
  userData?: Partial<Types.User>;
  deviceId?: string;
  country?: string;
  isSubscribed?: boolean;
  readerSettings: Types.ReaderSettings;
  readingProgress: { [bookId: string]: Types.ReadingProgress };
  bookshelf: string[];
  likedBooks: string[];
  hasSeenReaderTutorial: boolean;
  hasSeenOnboarding: boolean;
  hasSeenScrollTutorial: boolean;
  hasSeenNotificationPrompt: boolean;
  hasCompletedWebQuiz: boolean;
  webQuizAnswers: {
    genres: string[];
    readingApp: string;
    frustration: string;
    monthlySpend: string;
    frequency: string;
    series: string;
    mood: string;
  } | null;
  // Language
  contentLanguage: ContentLanguage;
  // Book state
  homeBooks: Types.Book[];
  currentBook?: Types.Book;
  currentGenerationState?: Types.GenerationState;
  currentChapter?: Types.AIChapter;
  currentChapterStatus?:
    | "ready"
    | "generating"
    | "queued"
    | "requires_previous"
    | "subscription_required";
  chapterCache: Record<string, Types.AIChapter>;
};

type Actions = {
  setUserToken: (token: string) => void;
  setHasHydrated: (status: boolean) => void;
  setIsSubscribed: (isSubscribed: boolean) => void;
  setDeviceId: (deviceId: string) => void;
  setLoginPromptDismissed: (loginPromptDismissed: boolean) => void;
  setContentLanguage: (lang: ContentLanguage) => void;
  setCountry: (country: string) => void;
  setWebSubscriptionPlans: (plans: State["webSubscriptionPlans"]) => void;
  library: () => {
    home: () => Promise<void>;
  };
  book: () => {
    get: (bookId: string) => Promise<void>;
    getGenerationStatus: (bookId: string) => Promise<void>;
  };
  chapter: () => {
    get: (params: { bookId: string; chapterNumber: number }) => Promise<void>;
    pollStatus: (params: {
      bookId: string;
      chapterNumber: number;
    }) => Promise<void>;
    prefetch: (params: {
      bookId: string;
      chapterNumber: number;
    }) => Promise<void>;
  };
  preference: () => {
    setReaderSettings: (payload: Partial<Types.ReaderSettings>) => void;
    saveProgress: (params: {
      bookId: string;
      chapterNumber: number;
      scrollPosition: number;
    }) => void;
    getProgress: (bookId: string) => Types.ReadingProgress | undefined;
    addToBookshelf: (bookId: string) => void;
    removeFromBookshelf: (bookId: string) => void;
    addToLikedBooks: (bookId: string) => void;
    removeFromLikedBooks: (bookId: string) => void;
    setHasSeenReaderTutorial: () => void;
    setHasSeenOnboarding: () => void;
    setHasSeenScrollTutorial: () => void;
    setHasSeenNotificationPrompt: () => void;
    setHasCompletedWebQuiz: () => void;
    setWebQuizAnswers: (answers: State["webQuizAnswers"]) => void;
  };
  user: () => {
    get: () => Promise<void>;
    profile: () => Promise<void>;
    remoteConfig: (params?: {
      queryParams?: { platform?: string; version?: string };
      callback?: (config: Types.RemoteConfig) => void;
    }) => Promise<void>;
    update: (updates: Partial<Types.User>) => Promise<void>;
    nativeSignIn: (params: { onSuccess?: () => void }) => Promise<void>;
  };
  subscription: () => {
    configureRevenueCat: (params?: {
      callback?: (isSubscribed: boolean) => void;
      onError?: (error: Error) => void;
    }) => Promise<void>;
    revenueCatPurchase: (params: {
      pkg: PurchasesPackage;
      utmSource?: string;
      callback?: () => void;
    }) => Promise<void>;
    revenueCatRestorePurchase: () => Promise<void>;
    stripeCheckout: (params: {
      subscriptionPlan: Types.SubscriptionPlan;
    }) => Promise<void>;
    onStripeCheckoutSuccess: (params: {
      router: ReturnType<typeof useRouter>;
    }) => Promise<void>;
    status: () => Promise<boolean>;
    stripeBillingPortal: () => Promise<void>;
  };
  reset: () => void;
};

export const initialState: State = {
  _hasHydrated: false,
  webSubscriptionPlans: {
    weekly: "mid_tier_weekly",
    annual: "mid_tier_annual",
  },
  userData: undefined,
  readerSettings: {
    fontSize: 18,
    lineHeight: 1.8,
    fontFamily: "serif",
    readerTheme: "sepia",
  },
  readingProgress: {},
  bookshelf: [],
  likedBooks: [],
  hasSeenReaderTutorial: false,
  hasSeenOnboarding: false,
  hasSeenScrollTutorial: false,
  hasSeenNotificationPrompt: false,
  hasCompletedWebQuiz: false,
  webQuizAnswers: null,
  contentLanguage: "en",
  homeBooks: [],
  chapterCache: {},
};

export const useUserSlice = create<State & Actions>()(
  persist(
    (set, get) => ({
      ...initialState,
      setCountry: (country) => {
        set({ country });
      },
      setLoginPromptDismissed: (loginPromptDismissed) => {
        set({ loginPromptDismissed });
      },
      setContentLanguage: (lang) => {
        set({ contentLanguage: lang, homeBooks: [], chapterCache: {} });
        get().library().home();
      },
      setWebSubscriptionPlans: (webSubscriptionPlans) => {
        set({ webSubscriptionPlans });
      },
      library: () => ({
        home: async () => {
          const lang = get().contentLanguage;
          const res = await serverClient.bookAsync({ type: "home", payload: { lang } });
          if (!res?.data?.homeBooks) return;

          set({ homeBooks: res.data.homeBooks });
        },
      }),
      book: () => ({
        get: async (bookId) => {
          const lang = get().contentLanguage;
          const res = await serverClient.bookAsync({
            type: "get",
            payload: { bookId, lang },
          });

          if (!res?.data?.book) return;

          set({ currentBook: res.data.book });
        },
        getGenerationStatus: async (bookId) => {
          const lang = get().contentLanguage;
          const res = await serverClient.bookAsync({
            type: "getGenerationStatus",
            payload: { bookId, lang },
          });

          if (res?.data?.generationState) {
            set({ currentGenerationState: res.data.generationState });
          }
        },
      }),
      chapter: () => ({
        get: async ({ bookId, chapterNumber }) => {
          const lang = get().contentLanguage;
          const cacheKey = `${bookId}-${chapterNumber}-${lang}`;
          const cached = get().chapterCache[cacheKey];

          if (cached) {
            set({ currentChapter: cached, currentChapterStatus: "ready" });
            return;
          }

          set({ currentChapterStatus: undefined });

          const res = await serverClient.bookAsync({
            type: "getAIChapter",
            payload: { bookId, chapterNumber, lang },
          });

          if (!res) return;

          if (res.error === "SubscriptionRequired") {
            set({ currentChapterStatus: "subscription_required" });
            return;
          }

          if (res.data?.status === "ready" && res.data?.aiChapter) {
            set({
              currentChapter: res.data.aiChapter,
              currentChapterStatus: "ready",
              chapterCache: {
                ...get().chapterCache,
                [cacheKey]: res.data.aiChapter,
              },
            });
          } else {
            set({
              currentChapterStatus: res.data?.status,
              currentGenerationState: res.data?.generationState,
            });
          }
        },
        pollStatus: async ({ bookId, chapterNumber }) => {
          const lang = get().contentLanguage;
          const res = await serverClient.bookAsync({
            type: "getAIChapter",
            payload: { bookId, chapterNumber, lang },
          });

          if (!res) return;

          if (res.data?.status === "ready" && res.data?.aiChapter) {
            const cacheKey = `${bookId}-${chapterNumber}-${lang}`;
            logFunnelEvent("[9] reader:chapter_generated", { bookId, chapterNumber });
            set({
              currentChapter: res.data.aiChapter,
              currentChapterStatus: "ready",
              chapterCache: {
                ...get().chapterCache,
                [cacheKey]: res.data.aiChapter,
              },
            });
          } else {
            set({
              currentChapterStatus: res.data?.status,
              currentGenerationState: res.data?.generationState,
            });
          }
        },
        prefetch: async ({ bookId, chapterNumber }) => {
          if (!useAuth.isAuthenticated()) return;

          const lang = get().contentLanguage;
          const cacheKey = `${bookId}-${chapterNumber}-${lang}`;
          if (get().chapterCache[cacheKey]) return;

          const res = await serverClient.bookAsync({
            type: "getAIChapter",
            payload: { bookId, chapterNumber, lang },
          });

          if (res?.data?.status === "ready" && res.data?.aiChapter) {
            set({
              chapterCache: {
                ...get().chapterCache,
                [cacheKey]: res.data.aiChapter,
              },
            });
          }
        },
      }),
      preference: () => ({
        setReaderSettings: (payload) => {
          const updated = { ...get().readerSettings, ...payload };
          set({ readerSettings: updated });

          serverClient
            .preferenceAsync({
              type: "set",
              payload: { preferences: { readerSettings: updated } },
            })
            .catch(() => {});
        },
        saveProgress: ({ bookId, chapterNumber, scrollPosition }) => {
          const lastReadAt = new Date().toISOString();
          set({
            readingProgress: {
              ...get().readingProgress,
              [bookId]: {
                lastChapter: chapterNumber,
                scrollPosition,
                lastReadAt,
              },
            },
          });

          if (!get().bookshelf.includes(bookId)) {
            set({ bookshelf: [...get().bookshelf, bookId] });
          }

          serverClient
            .preferenceAsync({
              type: "set",
              payload: {
                preferences: {
                  readingProgress: {
                    [bookId]: {
                      lastChapter: chapterNumber,
                      scrollPosition,
                      lastReadAt,
                    },
                  },
                  bookshelf: get().bookshelf,
                },
              },
            })
            .catch(() => {});
        },
        getProgress: (bookId) => {
          return get().readingProgress[bookId];
        },
        addToBookshelf: (bookId) => {
          if (!get().bookshelf.includes(bookId)) {
            const newBookshelf = [...get().bookshelf, bookId];
            set({ bookshelf: newBookshelf });

            serverClient
              .preferenceAsync({
                type: "set",
                payload: { preferences: { bookshelf: newBookshelf } },
              })
              .catch(() => {});
          }
        },
        removeFromBookshelf: (bookId) => {
          if (get().bookshelf.includes(bookId)) {
            const newBookshelf = get().bookshelf.filter((id) => id !== bookId);
            set({ bookshelf: newBookshelf });

            serverClient
              .preferenceAsync({
                type: "set",
                payload: { preferences: { bookshelf: newBookshelf } },
              })
              .catch(() => {});
          }
        },
        addToLikedBooks: (bookId) => {
          if (!get().likedBooks.includes(bookId)) {
            const newLikedBooks = [...get().likedBooks, bookId];
            set({ likedBooks: newLikedBooks });

            serverClient
              .preferenceAsync({
                type: "set",
                payload: { preferences: { likedBooks: newLikedBooks } },
              })
              .catch(() => {});
          }
        },
        removeFromLikedBooks: (bookId) => {
          if (get().likedBooks.includes(bookId)) {
            const newLikedBooks = get().likedBooks.filter(
              (id) => id !== bookId
            );
            set({ likedBooks: newLikedBooks });

            serverClient
              .preferenceAsync({
                type: "set",
                payload: { preferences: { likedBooks: newLikedBooks } },
              })
              .catch(() => {});
          }
        },
        setHasSeenReaderTutorial: () => {
          set({ hasSeenReaderTutorial: true });
        },
        setHasSeenOnboarding: () => {
          set({ hasSeenOnboarding: true });
        },
        setHasSeenScrollTutorial: () => {
          set({ hasSeenScrollTutorial: true });
        },
        setHasSeenNotificationPrompt: () => {
          set({ hasSeenNotificationPrompt: true });
        },
        setHasCompletedWebQuiz: () => {
          set({ hasCompletedWebQuiz: true });
        },
        setWebQuizAnswers: (answers: State["webQuizAnswers"]) => {
          set({ webQuizAnswers: answers });
        },
      }),
      user: () => ({
        remoteConfig: async (params?: {
          queryParams?: { platform?: string; version?: string };
          callback?: (config: Types.RemoteConfig) => void;
        }) => {
          const remoteConfig: Types.RemoteConfig =
            await serverClient.remoteConfigAsync(params);
          if (!remoteConfig) return;

          set({ remoteConfig });
          if (params?.callback) {
            params.callback(remoteConfig);
          }
        },
        get: async () => {
          if (!useAuth.isAuthenticated()) return;

          const user = await serverClient.userAsync({ type: "get" });
          if (!user?.data?.user) return;

          set({ userData: user.data.user as Types.User });
        },
        profile: async () => {
          await Promise.all([get().user().get(), get().user().remoteConfig()]);
        },
        update: async (updates) => {
          if (!useAuth.isAuthenticated()) return;

          set({
            userData: { ...get().userData, ...updates },
          });

          const res = await serverClient.userAsync({
            type: "update",
            payload: { updates },
          });

          if (!res?.data?.user) return;

          set({
            userData: res.data.user,
          });
        },
        nativeSignIn: async (params?: { onSuccess?: () => void }) => {
          useUISlice.getState().setAlertPromptSignIn({
            title: i18n.t("useAuth.title"),
            subtitle: i18n.t("useAuth.subtitle"),
            placeholder: i18n.t("useAuth.signInEmailPlaceholder"),
            shouldPersistBackdropClick: true,
            button: {
              text: i18n.t("useAuth.signInButton"),
              onPress: async (input) => {
                const email = input.trim().toLowerCase();
                const validEmail = useAuth.isValidEmail(email);

                if (!validEmail) {
                  return toast.error(i18n.t("useAuth.invalidEmail"));
                }

                useUISlice.getState().setAlertPromptSignIn({
                  ...useUISlice.getState().alertPromptSignIn,
                  onGoogleSignIn: undefined,
                  defaultValue: email,
                  loading: true,
                });

                const otpRequestResponse = await serverClient.userAsync({
                  type: "nativeSignIn",
                  payload: {
                    action: "request",
                    payload: { email: email.trim().toLowerCase() },
                  },
                });

                useUISlice.getState().setAlertPromptSignIn({
                  ...useUISlice.getState().alertPromptSignIn,
                  onGoogleSignIn: undefined,
                  loading: false,
                });

                if (otpRequestResponse?.error) {
                  return toast.error(i18n.t("useAuth.invalidEmail"));
                }

                useUISlice.getState().setAlertPromptSignIn({
                  title: i18n.t("useAuth.oneTimeCodeTitle"),
                  subtitle: i18n.t("useAuth.oneTimeCodeSubtitle", { email }),
                  placeholder: i18n.t("useAuth.oneTimeCodePlaceholder"),
                  shouldPersistBackdropClick: true,
                  button: {
                    text: i18n.t("useAuth.oneTimeCodeButton"),
                    onPress: async (input) => {
                      useUISlice.getState().setAlertPromptSignIn({
                        ...useUISlice.getState().alertPromptSignIn,
                        defaultValue: input,
                        loading: true,
                      });

                      const otpVerifyResponse = await serverClient.userAsync({
                        type: "nativeSignIn",
                        payload: {
                          action: "confirm",
                          payload: { oneTimeCode: parseInt(input) },
                        },
                      });

                      useUISlice.getState().setAlertPromptSignIn({
                        ...useUISlice.getState().alertPromptSignIn,
                        loading: false,
                      });

                      if (
                        otpVerifyResponse &&
                        otpVerifyResponse.error === "InvalidOneTimeCode"
                      ) {
                        return toast.error(i18n.t("useAuth.invalidCode"));
                      }

                      if (otpVerifyResponse && otpVerifyResponse.data) {
                        const { accessToken } = otpVerifyResponse.data;

                        await useAuth.onAuthSuccess(accessToken);

                        params?.onSuccess?.();
                      }
                    },
                  },
                });
              },
            },
          });
        },
      }),
      subscription: () => ({
        configureRevenueCat: async (params?: {
          callback?: (isSubscribed: boolean) => void;
          onError?: (error: Error) => void;
        }) => {
          if (!useAuth.isAuthenticated() || !isNativeApp()) return;

          const user = useAuth.currentAuthUser();

          Purchases.configure({
            appUserID: user?.userId,
            apiKey:
              getPlatform() === "ios"
                ? REVENUE_CAT_IOS_API_KEY
                : REVENUE_CAT_ANDROID_API_KEY,
          })
            .then(async () => {
              const setFirebaseAppInstanceID = async () => {
                try {
                  await import("@/fetchData/firebaseAnalytics").then(
                    async (firebaseAnalytics) => {
                      const firebaseAppInstanceID =
                        await firebaseAnalytics.getFirebaseAppInstanceId();
                      Purchases.setFirebaseAppInstanceID({
                        firebaseAppInstanceID,
                      });
                    }
                  );
                } catch (error) {
                  console.error(error);
                }
              };

              const [isSubscribed, customerInfo, offerings] = await Promise.all(
                [
                  get().subscription().status(),
                  Purchases.getCustomerInfo().then((res) => res.customerInfo),
                  Purchases.getOfferings(),
                  setFirebaseAppInstanceID(),
                ]
              );

              useUISlice.getState().setRevenueCat({
                packages:
                  offerings?.current?.availablePackages?.slice(0, 3) || [],
                customerInfo,
              });

              params?.callback?.(isSubscribed);

              if (getPlatform() === "ios") {
                try {
                  await Purchases.enableAdServicesAttributionTokenCollection();
                } catch (error) {
                  console.error(error);
                }
              }
            })
            .catch((error) => {
              console.error(error);
              params?.onError?.(error as Error);
            });
        },
        revenueCatPurchase: async ({ pkg, utmSource, callback }) => {
          if (!useAuth.isAuthenticated() || !isNativeApp()) return;

          try {
            const { customerInfo } = await Purchases.purchasePackage({
              aPackage: pkg,
            });

            if (customerInfo.activeSubscriptions.length > 0) {
              useUserSlice.getState().setIsSubscribed(true);
              logFunnelEvent("[11] paywall:purchase_completed", { packageIdentifier: pkg.identifier, utmSource });
              callback?.();
            }
          } catch (e: unknown) {
            if (serverClient.__DEV__) console.error(e);
          }
        },
        revenueCatRestorePurchase: async () => {
          if (!useAuth.isAuthenticated() || !isNativeApp()) return;

          const restorePurchaseResponse = await Purchases.restorePurchases();
          if (
            !restorePurchaseResponse?.customerInfo?.activeSubscriptions ||
            restorePurchaseResponse?.customerInfo?.activeSubscriptions
              ?.length === 0
          ) {
            toast.error(i18n.t("useUserSlice.noSubscriptionText"));
            return;
          }

          const isSubscribed =
            restorePurchaseResponse.customerInfo.activeSubscriptions.length > 0;
          set({ isSubscribed });

          toast.success(i18n.t("useUserSlice.restorePurchaseSuccess"));
        },
        stripeCheckout: async ({ subscriptionPlan }) => {
          if (!useAuth.isAuthenticated() || get().isSubscribed) return;

          const response = await serverClient.subscriptionAsync({
            type: "checkout",
            payload: { baseAppUrl: window.location.href, subscriptionPlan },
          });

          if (response?.data?.url) {
            window.open(response?.data?.url, "_self");
          }
        },
        status: async () => {
          if (!useAuth.isAuthenticated()) return;

          const res = await serverClient.subscriptionAsync({
            type: "status",
          });
          if (res?.data?.isActive === undefined) return;

          set({ isSubscribed: res?.data?.isActive });

          return res?.data?.isActive;
        },
        onStripeCheckoutSuccess: async (params) => {
          if (!useAuth.isAuthenticated()) return;
          const slug = params.router.query.checkout_success;
          if (!slug || slug !== "true") return;

          const fbq = await import("@/fetchData/fpixel");
          const gtag = await import("@/fetchData/gtag");

          const planPrices: Record<Types.SubscriptionPlan, number> = {
            low_tier_weekly: 4.99,
            mid_tier_weekly: 7.99,
            top_tier_weekly: 9.99,
            low_tier_monthly: 19.99,
            mid_tier_monthly: 29.99,
            top_tier_monthly: 39.99,
            low_tier_annual: 39.99,
            mid_tier_annual: 49.99,
            top_tier_annual: 59.99,
          };
          const value =
            planPrices[params.router.query.plan as Types.SubscriptionPlan];

          fbq.event("Subscribe", { currency: "USD", value: value.toString() });
          gtag.conversion({ value, transactionId: String(Date.now()) });
          gtag.event("purchase", {
            email: useAuth.currentAuthUser()?.email,
            plan: params.router.query.plan,
            currency: "USD",
            value: value.toString(),
          });

          useUISlice.getState().setAlertModal({
            title: i18n.t("alertWidgetPaywall.subscriptionSuccess"),
            subtitle: i18n.t(
              "alertWidgetPaywall.subscriptionSuccessDescription"
            ),
            hideDismissIcon: true,
            shouldPersistBackdropClick: true,
            button: {
              text: i18n.t("alertWidgetPaywall.subscriptionSuccessButton"),
              onPress: async () => {
                const url = new URL(window.location.href);
                url.searchParams.delete("checkout_success");
                params.router.replace(url.pathname + url.search);

                useUISlice.getState().setAlertModal({
                  ...useUISlice.getState().alertModal,
                  loading: true,
                });

                await get().subscription().status();
                useUISlice.getState().setAlertModal(undefined);
              },
            },
          });
        },
        stripeBillingPortal: async () => {
          if (!useAuth.isAuthenticated()) return;

          const response = await serverClient.subscriptionAsync({
            type: "billingPortal",
            payload: { baseAppUrl: window.location.href },
          });

          if (!response?.data?.url) return;

          window.open(response?.data?.url, "_self");
        },
      }),
      setHasHydrated: (status) => {
        set({ _hasHydrated: status });
      },
      setUserToken: (token) => {
        set({ userToken: token });
      },
      setIsSubscribed: (isSubscribed) => {
        set({ isSubscribed });
      },
      setDeviceId: (deviceId) => {
        set({ deviceId });
      },
      reset: () => {
        set({
          ...get(),
          userToken: undefined,
          userData: undefined,
          isSubscribed: false,
          homeBooks: [],
          currentBook: undefined,
          currentGenerationState: undefined,
          currentChapter: undefined,
          currentChapterStatus: undefined,
          chapterCache: {},
        });
      },
    }),
    {
      name: `purelynovels-${Capacitor.getPlatform()}-user-storage`,
      storage: createJSONStorage(() => storage),
      partialize: (state) => ({
        _hasHydrated: state._hasHydrated,
        userToken: state.userToken,
        deviceId: state.deviceId,
        country: state.country,
        contentLanguage: state.contentLanguage,
        isSubscribed: state.isSubscribed,
        loginPromptDismissed: state.loginPromptDismissed,
        webSubscriptionPlans: state.webSubscriptionPlans,
        userData: state.userData,
        readerSettings: state.readerSettings,
        readingProgress: state.readingProgress,
        bookshelf: state.bookshelf,
        likedBooks: state.likedBooks,
        hasSeenReaderTutorial: state.hasSeenReaderTutorial,
        hasSeenOnboarding: state.hasSeenOnboarding,
        hasSeenScrollTutorial: state.hasSeenScrollTutorial,
        hasSeenNotificationPrompt: state.hasSeenNotificationPrompt,
        hasCompletedWebQuiz: state.hasCompletedWebQuiz,
        webQuizAnswers: state.webQuizAnswers,
      }),
      onRehydrateStorage: () => (_state, error) => {
        useUserSlice.getState().setHasHydrated(true);

        if (error) {
          console.warn("Zustand rehydration error:", error);
        }
      },
    }
  )
);
